import { format } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import { LoadPanel } from "devextreme-react";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import notify from "../../../lib/notify";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  acceptWorkingHours,
  getWorkingHoursPreview,
  WorkingHoursPreviewDay,
} from "../../../api/acceptedWorkingHours";

type Props = {
  visible: boolean;
  onHiding: () => void;
  onAccepted: () => void;
};

const formatTime = (value: string) => value.slice(0, 5);

// Liczba minut z "HH:mm[:ss]" od początku doby.
const minutesFromTime = (value: string) => {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

// Czas trwania wejścia w minutach; gdy godzina zakończenia jest wcześniejsza
// niż rozpoczęcia (zmiana nocna, np. 22:00-06:00), dodajemy pełną dobę.
const entryDurationMinutes = (from: string, to: string) => {
  const diff = minutesFromTime(to) - minutesFromTime(from);
  return diff < 0 ? diff + 24 * 60 : diff;
};

// Formatuje minuty jako "gg:mm".
const formatMinutesAsHours = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

export const AcceptWorkingHoursDialog = ({
  visible,
  onHiding,
  onAccepted,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [days, setDays] = useState<WorkingHoursPreviewDay[]>([]);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  const formatDate = (value: string) =>
    format(new Date(value), "dd.MM.yyyy (EEEEEE)", {
      locale: i18n.language === "en" ? enUS : pl,
    });

  const loadPreview = useCallback(async () => {
    setLoaded(false);
    const response = await getWorkingHoursPreview();
    if (response.isOk) {
      setDays(response.data);
    } else {
      notify(response.error, "error");
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (visible) {
      loadPreview();
    } else {
      setLoaded(false);
    }
  }, [visible, loadPreview]);

  // Suma przepracowanych godzin (tylko obecności, bez nieobecności).
  const totalWorkedMinutes = days.reduce(
    (sum, day) =>
      sum +
      day.workEntries.reduce(
        (daySum, entry) => daySum + entryDurationMinutes(entry.from, entry.to),
        0,
      ),
    0,
  );

  const handleAccept = async () => {
    setAccepting(true);
    const response = await acceptWorkingHours();
    setAccepting(false);

    if (response.isOk) {
      notify(t("acceptWorkingHours.accepted"), "success");
      onAccepted();
    } else {
      notify(response.error, "error");
    }
  };

  return (
    <>
      <LoadPanel
        shading
        shadingColor="rgba(0,0,0,0.3)"
        visible={visible && !loaded}
      />
      <Popup
        visible={visible && loaded}
        onHiding={onHiding}
        maxWidth={900}
        height="auto"
        maxHeight="85vh"
        title={t("acceptWorkingHours.dialogTitle")}
        showCloseButton
        dragEnabled
      >
        <div className="flex flex-col gap-4">
          <div className="p-3 rounded-md bg-yellow-100 text-yellow-900 dark:bg-yellow-200 dark:text-black">
            {t("acceptWorkingHours.reportIrregularities")}
          </div>
          <div>
            {t("acceptWorkingHours.totalWorkedHours", {
              total: formatMinutesAsHours(totalWorkedMinutes),
            })}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm align-middle border border-solid border-gray-200 dark:border-gray-700">
              <colgroup>
                <col style={{ width: 175 }} />
                <col style={{ width: 140 }} />
                <col />
              </colgroup>
              <thead>
                <tr className="bg-gray-100 dark:bg-[#333]">
                  <th className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 text-left">
                    {t("acceptWorkingHours.table.date")}
                  </th>
                  <th className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 text-left">
                    {t("acceptWorkingHours.table.hours")}
                  </th>
                  <th className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 text-left">
                    {t("acceptWorkingHours.table.project")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {days.map((day) => {
                  // Dzień może mieć jednocześnie wejścia pracy i nieobecności
                  // (np. 4h pracy + 4h urlopu) - każdy z nich to osobny wiersz.
                  // Sortujemy chronologicznie po godzinie rozpoczęcia (od).
                  const rows = [
                    ...day.workEntries.map((entry) => ({
                      from: formatTime(entry.from),
                      hours: `${formatTime(entry.from)}-${formatTime(entry.to)}`,
                      label: entry.project || "—",
                    })),
                    ...day.absences.map((absence) => ({
                      from: absence.hourFrom
                        ? formatTime(absence.hourFrom)
                        : "",
                      hours:
                        absence.hourFrom && absence.hourTo
                          ? `${formatTime(absence.hourFrom)}-${formatTime(
                              absence.hourTo,
                            )}`
                          : "",
                      label: absence.name,
                    })),
                  ].sort((a, b) => a.from.localeCompare(b.from));

                  // Hover obejmuje cały blok dnia (łącznie z komórką daty
                  // rozciągniętą przez rowSpan) - sterujemy nim per dzień.
                  const isHovered = hoveredDate === day.date;
                  const rowClass = isHovered
                    ? "bg-gray-100 dark:bg-[#333]"
                    : "";
                  const hoverHandlers = {
                    onMouseEnter: () => setHoveredDate(day.date),
                    onMouseLeave: () => setHoveredDate(null),
                  };

                  if (rows.length === 0) {
                    return (
                      <tr
                        key={day.date}
                        className={rowClass}
                        {...hoverHandlers}
                      >
                        <td className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 whitespace-nowrap font-semibold">
                          {formatDate(day.date)}
                        </td>
                        <td
                          colSpan={2}
                          className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 text-gray-400"
                        >
                          —
                        </td>
                      </tr>
                    );
                  }

                  return rows.map((row, index) => (
                    <tr
                      key={`${day.date}-${index}`}
                      className={rowClass}
                      {...hoverHandlers}
                    >
                      {index === 0 ? (
                        <td
                          rowSpan={rows.length}
                          className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 whitespace-nowrap font-semibold"
                        >
                          {formatDate(day.date)}
                        </td>
                      ) : null}
                      <td className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2 whitespace-nowrap">
                        {row.hours}
                      </td>
                      <td className="border border-solid border-gray-200 dark:border-gray-700 px-3 py-2">
                        {row.label}
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        </div>
        <ToolbarItem
          location="after"
          toolbar="bottom"
          widget="dxButton"
          options={{
            text: accepting
              ? t("common.sending")
              : t("acceptWorkingHours.accept"),
            type: "default",
            stylingMode: "contained",
            disabled: accepting,
            onClick: handleAccept,
          }}
        />
      </Popup>
    </>
  );
};
